import { useEffect, useState } from 'react';
import type { ClientBookMode, Service, Staff } from '@salonomy/types';
import { Button, Card, Field, Pill, TextInput, Toggle } from '@salonomy/ui';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { useLocations } from '../data/locations.js';
import { useSalon, useStaff, useUpdateSalon } from '../data/salon.js';
import { useInviteStaff, useUpdateStaff } from '../data/staff.js';
import { useServices, useUpdateService } from '../data/services.js';
import { ServiceModal } from '../features/services/ServiceModal.js';
import { PricingLevelsPanel } from '../features/services/PricingLevelsPanel.js';
import { ClosedDatesPanel } from '../features/scheduling/ClosedDatesPanel.js';
import { RotaEditor } from '../features/scheduling/RotaEditor.js';
import { StaffModal } from '../features/team/StaffModal.js';
import { LocationsPanel } from '../features/locations/LocationsPanel.js';
import { GdprPanel } from '../features/gdpr/GdprPanel.js';
import { DeveloperPanel } from '../features/developer/DeveloperPanel.js';
import { BookingPolicyEditor } from '../features/policy/BookingPolicyEditor.js';
import { RecurringSeriesPanel } from '../features/recurring/index.js';
import { WaitlistPanel } from '../features/waitlist/WaitlistPanel.js';
import { BillingPanel } from '../features/billing/index.js';
import { ResidenciesPanel, MyResidenciesPanel } from '../features/residency/index.js';
import { PracticePanel, BookingHandlePanel } from '../features/practice/index.js';
import { LoyaltySettingsPanel } from '../features/loyalty/LoyaltySettingsPanel.js';
import { ConsentTemplatesPanel } from '../features/consent/ConsentTemplatesPanel.js';
import { SecurityPanel } from '../features/security/SecurityPanel.js';
import { useAuth } from '../lib/auth.js';
import { money } from '../lib/format.js';
import { canEditStaffRole, ROLE_LABEL } from '../lib/rbac.js';
import { useToast } from '../lib/toast.js';

const BOOK_MODES: Array<{ value: ClientBookMode; label: string; blurb: string }> = [
  { value: 'shared', label: 'One shared book', blurb: 'Every client is visible salon-wide.' },
  {
    value: 'sealed',
    label: 'Sealed per renter',
    blurb: 'Each chair renter’s clients are theirs alone — invisible to everyone else.',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    blurb: 'Set per person — shared for employed staff, sealed for renters.',
  },
];

/** A small "this surface isn't designed yet" flag — honest signpost, no invented visual language. */
function DesignPending() {
  return (
    <Pill tone="soft" className="mono">
      Design pending
    </Pill>
  );
}

/** A reserved-but-not-built section. Rendered, never absent, so the shape of "done" is visible. */
function ComingSoon({
  title,
  blurb,
  badge = 'Coming soon',
}: {
  title: string;
  blurb: string;
  badge?: string;
}) {
  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {blurb}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <DesignPending />
          <Pill tone="neutral">{badge}</Pill>
        </div>
      </div>
    </Card>
  );
}

export function SettingsPage() {
  const { isManager, user } = useAuth();
  const { data: salon } = useSalon();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: locations } = useLocations();
  const updateSalon = useUpdateSalon();
  const updateStaff = useUpdateStaff();
  const inviteStaff = useInviteStaff();
  const updateService = useUpdateService();
  const toast = useToast();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [mode, setMode] = useState<ClientBookMode>('shared');
  const [vatReg, setVatReg] = useState(false);
  const [vatNumber, setVatNumber] = useState('');

  // Modal state for Team + Services CRUD.
  const [staffModal, setStaffModal] = useState<{ existing?: Staff } | null>(null);
  const [serviceModal, setServiceModal] = useState<{ existing?: Service } | null>(null);
  const [deactivateStaff, setDeactivateStaff] = useState<Staff | null>(null);
  const [archiveService, setArchiveService] = useState<Service | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (salon) {
      setName(salon.name);
      setSlug(salon.slug);
      setMode(salon.clientBookMode);
      setVatReg(salon.vatRegistered);
      setVatNumber(salon.vatNumber ?? '');
    }
  }, [salon]);

  const tz = salon?.timezone ?? 'Europe/London';

  // A chair_renter (or reception/stylist) gets a read-only profile view, never the roster controls —
  // but they DO manage their own time off and hour exceptions (mirror of the API's manager-or-self
  // rule). Reception has no staff record / chair, so they only see the profile card.
  if (!isManager) {
    const me = (staff ?? []).find((s) => s.id === user?.staffId);
    const managesOwnRota = me != null && ['owner', 'stylist', 'chair_renter'].includes(me.role);
    return (
      <>
        <div className="topbar">
          <div className="topbar__title">Settings</div>
        </div>
        <div className="screen-scroll">
          <div
            className="screen-pad"
            style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 880 }}
          >
            {me ? (
              <Card>
                <div className="section-title">Your profile</div>
                <div className="settings-list" style={{ marginTop: 8 }}>
                  <div className="settings-row">
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      Name
                    </div>
                    <div style={{ fontWeight: 600 }}>{me.displayName}</div>
                  </div>
                  <div className="settings-row">
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      Role
                    </div>
                    <Pill tone="neutral">{ROLE_LABEL[me.role] ?? me.role}</Pill>
                  </div>
                  {me.renterId ? (
                    <div className="settings-row">
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        Client book
                      </div>
                      <Pill tone="soft">Sealed — only you can see your clients</Pill>
                    </div>
                  ) : null}
                  {me.title ? (
                    <div className="settings-row">
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        Title
                      </div>
                      <div>{me.title}</div>
                    </div>
                  ) : null}
                </div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
                  Only an owner or manager can change salon settings or the team roster.
                </div>
              </Card>
            ) : (
              <div className="muted">Only an owner or manager can change salon settings.</div>
            )}

            {/* WS11 brief item 2/3 — a practitioner's "Where you work": every salon they hold a
                membership in (residencies) plus their own studio, tagged by salon. Self-fetching via
                useResidencies (the caller's OWN cross-salon memberships), read-only for any signed-in
                user. The identity union the unified "My week" diary sits on top of. */}
            <MyResidenciesPanel />

            {managesOwnRota ? <RotaEditor staff={[me!]} tz={tz} lockedStaffId={me!.id} /> : null}

            {/* OPTIONAL TOTP 2FA — per ACCOUNT, not per role, so every signed-in human sees it here. */}
            <SecurityPanel />

            {/* A chair_renter can export / erase their own sealed book from Settings. GdprPanel
                self-gates: renters get whole-book controls; stylist/reception get an honest note. */}
            <GdprPanel />

            {/* WS11 brief item 1 (ADR-0013) — open your OWN one-chair studio on this account. The prime
                audience (a chair_renter) is a non-manager, so this lives in the read-only branch too.
                Self-fetching/self-gating by the human, not the current tenant role. */}
            <PracticePanel />

            {/* WS10(b) — claim/manage your GLOBAL personal booking page (book.salonomy.com/p/<handle>). Like
                the practice surface it gates on the HUMAN, not the tenant role, so a chair_renter sees it
                here too. Self-fetching/self-gating; 404-on-GET means simply "unclaimed". */}
            <BookingHandlePanel />
          </div>
        </div>
      </>
    );
  }

  async function save() {
    try {
      await updateSalon.mutateAsync({
        name: name.trim(),
        slug: slug.trim(),
        clientBookMode: mode,
        vatRegistered: vatReg,
        vatNumber: vatReg ? vatNumber.trim() || null : null,
      });
      toast.success('Settings saved');
    } catch {
      toast.error('Could not save settings');
    }
  }

  async function confirmDeactivate() {
    if (!deactivateStaff) return;
    setBusy(true);
    try {
      await updateStaff.mutateAsync({ id: deactivateStaff.id, body: { active: false } });
      toast.success(`${deactivateStaff.displayName} deactivated`);
      setDeactivateStaff(null);
    } catch {
      toast.error('Could not deactivate this member.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmArchive() {
    if (!archiveService) return;
    setBusy(true);
    try {
      await updateService.mutateAsync({ id: archiveService.id, body: { active: false } });
      toast.success(`${archiveService.name} archived`);
      setArchiveService(null);
    } catch {
      toast.error('Could not archive this service.');
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite(member: Staff) {
    if (!member.email) {
      toast.error('Add an email to this member first, then send the invite.');
      return;
    }
    try {
      await inviteStaff.mutateAsync({ id: member.id });
      toast.success(`Invite emailed to ${member.email}`);
    } catch {
      toast.error('Could not send the invite.');
    }
  }

  const serviceCategories = Array.from(
    new Set((services ?? []).map((s) => s.category).filter((c): c is string => Boolean(c))),
  ).sort();

  return (
    <>
      <div className="topbar">
        <div className="topbar__title">Settings</div>
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 880 }}
        >
          <Card>
            <div className="section-title">Salon</div>
            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12 }}
            >
              <Field label="Name">
                <TextInput value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Queue slug" hint={`salonomy.com/q/${slug || '…'}`}>
                <TextInput value={slug} onChange={(e) => setSlug(e.target.value)} />
              </Field>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <Toggle checked={vatReg} onChange={setVatReg} label="VAT registered" />
              {vatReg ? (
                <TextInput
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder="GB123456789"
                  style={{ width: 180 }}
                />
              ) : null}
            </div>
          </Card>

          <Card>
            <div className="section-title">Client book</div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Choose at setup, change later. Sealed isolation is enforced by database row-level
              security, not a feature flag (ADR-0005).
            </div>
            <div className="comms-policies">
              {BOOK_MODES.map((m) => (
                <button
                  key={m.value}
                  className={`policy${mode === m.value ? ' policy--active' : ''}`}
                  onClick={() => setMode(m.value)}
                >
                  <div className="policy__title">
                    {m.label}
                    {mode === m.value ? <Pill tone="cobalt">Selected</Pill> : null}
                  </div>
                  <div className="policy__blurb">{m.blurb}</div>
                </button>
              ))}
            </div>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={save} disabled={updateSalon.isPending}>
              Save changes
            </Button>
          </div>

          {/* ── Team ── */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="section-title">Team</div>
              <Button size="sm" onClick={() => setStaffModal({})}>
                Add team member
              </Button>
            </div>
            <div className="settings-list">
              {(staff ?? []).map((s) => {
                const canEdit = canEditStaffRole(user?.role ?? 'receptionist', s.role);
                return (
                  <div key={s.id} className="settings-row">
                    <div>
                      <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        {s.title ?? ROLE_LABEL[s.role] ?? s.role}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Pill tone="neutral">{ROLE_LABEL[s.role] ?? s.role}</Pill>
                      {s.renterId ? <Pill tone="soft">Sealed book</Pill> : null}
                      {!s.active ? <Pill tone="danger">Inactive</Pill> : null}
                      {canEdit ? (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setStaffModal({ existing: s })}
                          >
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => sendInvite(s)}>
                            Send invite
                          </Button>
                          {s.active ? (
                            <Button size="sm" variant="ghost" onClick={() => setDeactivateStaff(s)}>
                              Deactivate
                            </Button>
                          ) : null}
                        </>
                      ) : (
                        <span
                          className="muted"
                          style={{ fontSize: 11.5 }}
                          title="Only the owner can edit the owner record"
                        >
                          Owner-only
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* WS11 brief item 2 — chair-renter residencies: invite an existing-account practitioner to
              rent a chair here, and end a residency with typed confirmation (salon history + the
              renter's sealed book preserved). Sits beside Team because a residency is a renter seat in
              this salon. Self-fetching/self-gating (owner/manager); a non-manager gets an honest note. */}
          <ResidenciesPanel />

          {/* ── Services ── */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="section-title">Services</div>
              <Button size="sm" onClick={() => setServiceModal({})}>
                Add service
              </Button>
            </div>
            <div className="settings-list">
              {(services ?? []).map((s) => (
                <div key={s.id} className="settings-row">
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {s.name}
                      {!s.active ? (
                        <Pill tone="danger" className="mono">
                          Archived
                        </Pill>
                      ) : null}
                    </div>
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {s.durationMin} min{s.category ? ` · ${s.category}` : ''}
                      {s.requiresPatchTest ? ' · patch test' : ''}
                      {!s.onlineBookable ? ' · in-salon only' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="mono">{money(s.priceFrom)}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setServiceModal({ existing: s })}
                    >
                      Edit
                    </Button>
                    {s.active ? (
                      <Button size="sm" variant="ghost" onClick={() => setArchiveService(s)}>
                        Archive
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* WS9 phase 1 — salon-wide pricing levels (seniority rates) + per-staff level assignment.
              Sits beside Services because it governs how every service's price resolves by stylist;
              the per-service variant × level matrix lives inside each service's edit modal. */}
          <PricingLevelsPanel staff={staff ?? []} />

          {/* WS-13 loyalty (LRU-06) — the per-salon program config: enable, earn model (points-per-spend
              vs visit stamps), the rate fields, minimum to redeem, expiry. Manager-only (self-gates via
              useAuth + the canEdit flag); points are a non-monetary perk redeemed as a discount at the
              till, and every figure shown there is the server's. */}
          <LoyaltySettingsPanel />

          {/* WS-13 Feature 4 — consent & consultation FORMS (LRU-07): the manager-only template editor.
              List every version, create a new form, edit (a wording change forks a NEW version
              server-side; signed records keep their frozen copy), and deactivate with typed confirmation.
              Self-gates via useAuth().isManager; staff capture against these on the client record. */}
          <ConsentTemplatesPanel />

          {/* ── Rota: base weekly working hours + time off + per-date hour exceptions ── */}
          {/* WS6/WS7: the rota editor now drives base weekly hours (WorkingHoursGrid, composed inside
              RotaEditor) AND date-specific time off / hour exceptions, plus salon closed dates. These
              feed the diary + availability engine. */}
          <RotaEditor staff={staff ?? []} tz={tz} selfStaffId={user?.staffId ?? null} />
          <ClosedDatesPanel tz={tz} />

          {/* WS6 item 4 — locations CRUD (list/create/edit + inline staff reassignment). */}
          <LocationsPanel locations={locations ?? []} staff={staff ?? []} />

          {/* WS6 item 5 — privacy & data: per-client + whole-book export/erasure, typed confirmation,
              online-only erasure, sealed-book legibility. Self-gating via useAuth(). */}
          <GdprPanel />

          {/* WS6 — developer & integrations: PATs (create/revoke), webhooks CRUD + delivery log +
              test ping, Stripe Connect status, comms provider health. Self-gating (owner/manager). */}
          <DeveloperPanel />

          {/* OPTIONAL TOTP 2FA — per ACCOUNT, not per role. Shown to every signed-in human. */}
          <SecurityPanel />

          {/* WS7 phase 2 — booking policies: deposit / cancellation window / no-show fee, per-service
              overrides, explicit arm-auto-charge opt-in, live plain-English preview. */}
          <BookingPolicyEditor />

          {/* WS7 phase 3 — recurring appointments: cadence creator with conflict preview, edit-series
              vs edit-this-one (detach), cancel-remaining. Self-fetching, self-gating. */}
          <RecurringSeriesPanel />

          {/* WS7 phase 4 — appointment waitlist: per-day depth, grouped entries, join + remove. A
              cancellation auto-offers the freed slot to the best match. */}
          <WaitlistPanel staff={staff ?? []} services={services ?? []} tz={tz} />

          {/* TODO(WS6): onboarding wizard — first-run guided flow composing the screens above
              (basics → book mode → locations → team → services → hours → queue slug → go-live). */}
          <ComingSoon
            title="Onboarding wizard"
            blurb="A guided first-run flow that walks a new salon from basics to go-live, reusing the screens above."
          />

          {/* WS10 — Salonomy's OWN SaaS subscription billing: the lifecycle state, the per-chair line
              with the staff behind the count, the itemised "why is this invoice this amount" breakdown,
              invoice history, payment-method + plan change, the dunning/read-only banner, and one-click
              cancel. Owner-only (the API gates every billing route to the owner; the panel mirrors that
              and shows a non-owner an honest note rather than a bare 403). Every figure is server-derived
              — no money/VAT/proration math on the client. Design pending (brief 10 §design). */}
          <BillingPanel isOwner={user?.role === 'owner'} />

          {/* WS11 brief item 1 (ADR-0013) — open your OWN one-chair studio on this account. A manager
              is a practitioner too (it triggers a tenant switch, not a salon-roster change), so the
              "Your own practice" surface is shown here as well as in the practitioner branch. */}
          <PracticePanel />

          {/* WS10(b) — a manager is a bookable practitioner too, so their personal booking page sits here
              alongside "Your own practice". Self-fetching/self-gating on the human; 404-on-GET = unclaimed. */}
          <BookingHandlePanel />
        </div>
      </div>

      {staffModal ? (
        <StaffModal
          existing={staffModal.existing}
          locations={locations ?? []}
          clientBookMode={salon?.clientBookMode ?? 'shared'}
          onClose={() => setStaffModal(null)}
        />
      ) : null}

      {serviceModal ? (
        <ServiceModal
          existing={serviceModal.existing}
          categories={serviceCategories}
          onClose={() => setServiceModal(null)}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(deactivateStaff)}
        title="Deactivate team member"
        confirmLabel="Deactivate"
        confirmPhrase={deactivateStaff?.displayName}
        busy={busy}
        onConfirm={confirmDeactivate}
        onClose={() => setDeactivateStaff(null)}
      >
        <p style={{ margin: '0 0 8px' }}>
          Deactivating <strong>{deactivateStaff?.displayName}</strong> removes them from the diary
          chair list and stops new bookings going to them. Existing appointments are not deleted.
        </p>
        <p style={{ margin: 0 }}>You can reactivate them later by editing their profile.</p>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(archiveService)}
        title="Archive service"
        confirmLabel="Archive"
        confirmPhrase={archiveService?.name}
        busy={busy}
        onConfirm={confirmArchive}
        onClose={() => setArchiveService(null)}
      >
        <p style={{ margin: 0 }}>
          Archiving <strong>{archiveService?.name}</strong> hides it from new bookings and the
          online booking page. Past appointments that used it are unaffected.
        </p>
      </ConfirmModal>
    </>
  );
}
